import { useState } from "react";
import { Package, Navigation, MapPin, Phone, CheckCircle, Star, Map as MapIcon, ChevronDown, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import OrderMiniRouteMap from "./OrderMiniRouteMap";
import RouteSplitScreen from "./RouteSplitScreen";

// Lien WhatsApp direct (numéros sénégalais à 9 chiffres → indicatif 221)
const waLink = (phone) => {
  let d = (phone || "").replace(/\D/g, "").replace(/^00/, "");
  if (!d) return null;
  if (d.length === 9) d = `221${d}`;
  return `https://wa.me/${d}`;
};

/**
 * Génère une URL universelle d'ouverture de Google Maps (fonctionne aussi avec Waze
 * via le picker système iOS/Android). Utilise les coordonnées GPS si disponibles,
 * sinon l'adresse texte URL-encodée.
 */
const buildNavUrl = (lat, lng, address) => {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  }
  if (address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
  }
  return null;
};

/**
 * Détermine la destination de navigation selon l'étape de la commande :
 *  • assigned    → pickup (le livreur va chercher le colis)
 *  • picked_up   → delivery (en route vers le destinataire)
 *  • in_transit  → delivery (même chose, étape finale)
 */
const getNavTarget = (order) => {
  if (order.status === "assigned") {
    return {
      label: "RETRAIT",
      address: order.pickup_address,
      lat: order.pickup_latitude,
      lng: order.pickup_longitude,
    };
  }
  return {
    label: "LIVRAISON",
    address: order.delivery_address,
    lat: order.delivery_latitude,
    lng: order.delivery_longitude,
  };
};

const STATUS_BADGES = {
  assigned: { bg: "bg-sky-400/20 text-sky-300", label: "Assignée" },
  picked_up: { bg: "bg-sky-400/20 text-sky-300", label: "Récupérée" },
  in_transit: { bg: "bg-[#D4AF37]/20 text-[#D4AF37]", label: "En route" },
  delivered: { bg: "bg-emerald-100 text-emerald-800", label: "Livrée" },
  cancelled: { bg: "bg-red-500/20 text-red-300", label: "Annulée" },
};

const getStatusBadge = (status) =>
  STATUS_BADGES[status] || { bg: "bg-gray-100 text-gray-800", label: status };

const CargoDetails = ({ order }) => {
  if (order.order_type !== "cargo" && order.type !== "cargo") return null;
  return (
    <div
      className="mb-3 p-3 rounded-xl bg-[#003594]/15 border border-[#003594]/40 text-sm flex flex-wrap gap-x-5 gap-y-1.5 text-slate-100"
      data-testid="cargo-details"
    >
      {order.volume_m3 > 0 && (
        <span className="flex items-center gap-1.5">
          <strong className="text-white">Volume:</strong> {order.volume_m3} m³
        </span>
      )}
      {order.weight_kg > 0 && (
        <span className="flex items-center gap-1.5">
          <strong className="text-white">Poids:</strong> {order.weight_kg} kg
        </span>
      )}
      {order.helpers_count > 0 && (
        <span className="flex items-center gap-1.5">
          <strong className="text-white">Aides:</strong> {order.helpers_count} pers.
        </span>
      )}
      {order.has_fragile && (
        <span className="flex items-center gap-1.5 text-yellow-400 font-bold">⚠ Fragile</span>
      )}
      {!order.has_elevator && (order.pickup_floor > 0 || order.delivery_floor > 0) && (
        <span className="flex items-center gap-1.5">
          <strong className="text-white">Étages:</strong> {order.pickup_floor || 0} ↔{" "}
          {order.delivery_floor || 0} (sans ascenseur)
        </span>
      )}
      {order.deposit_status === "paid" && (
        <span className="flex items-center gap-1.5 text-emerald-400 font-black">
          ✓ Acompte {order.deposit_amount?.toLocaleString()} FCFA payé
        </span>
      )}
    </div>
  );
};

const PipelineActions = ({ order, onAdvance }) => {
  if (order.status === "assigned") {
    return (
      <div className="space-y-2">
        <div className="flex items-start gap-2 text-sm text-white bg-[#003594]/20 border border-[#003594]/40 rounded-xl p-3 mb-2">
          <Navigation className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
          <span>
            <strong className="text-[#D4AF37] uppercase text-[10px] tracking-widest block">
              Départ
            </strong>
            {order.pickup_address}
          </span>
        </div>
        <Button
          onClick={() => onAdvance(order.id, "picked_up")}
          className="w-full bg-gradient-to-r from-[#003380] to-[#003594] hover:opacity-90 text-white rounded-full font-semibold"
          data-testid={`pickup-order-${order.id}`}
        >
          <Package className="w-4 h-4 mr-2" /> J'ai récupéré le colis
        </Button>
      </div>
    );
  }
  if (order.status === "picked_up") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-[#003594] bg-[#003594]/10 rounded-lg p-2 mb-2">
          <Navigation className="w-4 h-4" />
          <span>
            En route vers: <strong>{order.delivery_address}</strong>
          </span>
        </div>
        <Button
          onClick={() => onAdvance(order.id, "in_transit")}
          className="w-full bg-gradient-to-r from-[#003594] to-[#D4AF37] hover:opacity-90 text-white rounded-full font-semibold"
          data-testid={`intransit-order-${order.id}`}
        >
          <Navigation className="w-4 h-4 mr-2" /> Je suis en route
        </Button>
      </div>
    );
  }
  if (order.status === "in_transit") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-[#D4AF37] bg-[#D4AF37]/10 rounded-lg p-2 mb-2">
          <MapPin className="w-4 h-4" />
          <span>
            Destination: <strong>{order.delivery_address}</strong>
          </span>
        </div>
        <Button
          onClick={() => onAdvance(order.id, "delivered")}
          className="w-full bg-gradient-to-r from-[#D4AF37] to-[#003594] hover:opacity-90 text-white rounded-full font-semibold"
          data-testid={`deliver-order-${order.id}`}
        >
          <CheckCircle className="w-4 h-4 mr-2" /> Livraison terminée
        </Button>
      </div>
    );
  }
  return null;
};

const RatingBlock = ({
  order,
  rated,
  ratingOrderId,
  ratingScore,
  onStartRating,
  onSetScore,
  onSubmit,
  onCancel,
}) => {
  if (order.status !== "delivered") return null;
  return (
    <div className="space-y-2">
      <div className="text-center text-green-600 font-medium py-1">
        <CheckCircle className="w-4 h-4 inline mr-1" />
        Livré le {new Date(order.delivered_at).toLocaleDateString("fr-FR")}
      </div>
      {!rated && ratingOrderId !== order.id && (
        <Button
          onClick={() => onStartRating(order.id)}
          variant="outline"
          size="sm"
          className="w-full rounded-full border-yellow-400 text-yellow-700 hover:bg-yellow-50"
          data-testid={`rate-client-${order.id}`}
        >
          <Star className="w-4 h-4 mr-1" /> Noter le client
        </Button>
      )}
      {ratingOrderId === order.id && (
        <div className="bg-yellow-50 rounded-xl p-3 space-y-2">
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} onClick={() => onSetScore(s)}>
                <Star
                  className={`w-7 h-7 ${
                    s <= ratingScore ? "text-yellow-500 fill-yellow-500" : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full"
              disabled={ratingScore < 1}
              onClick={() => onSubmit(order)}
              data-testid={`submit-client-rating-${order.id}`}
            >
              Valider
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={onCancel}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
      {rated && <p className="text-center text-xs text-green-600">Client noté !</p>}
    </div>
  );
};

/**
 * Detailed card for a single delivery order: header, cargo details,
 * recipient info, call CTA, pipeline action button, rating flow.
 */
export const OrderDetailsCard = ({
  order,
  rated,
  ratingOrderId,
  ratingScore,
  onAdvance,
  onStartRating,
  onSetScore,
  onSubmitRating,
  onCancelRating,
}) => {
  const badge = getStatusBadge(order.status);
  const isCargo = order.order_type === "cargo" || order.type === "cargo";
  const phone = order.recipient_phone || order.customer_phone;
  const navTarget = getNavTarget(order);
  const hasDestGPS = navTarget.lat != null && navTarget.lng != null;
  const [mapOpen, setMapOpen] = useState(order.status === "in_transit"); // ouvert par défaut quand en route
  const [splitHidden, setSplitHidden] = useState(false);
  const split = order.status === "in_transit" && hasDestGPS && !splitHidden;
  const wa = waLink(phone);

  const card = (
    <div
      className="border-2 border-white/10 rounded-2xl p-5 bg-[#0F172A] shadow-xl"
      data-testid={`my-order-${order.id}`}
    >
      {order.order_type === "food" && order.restaurant_status && (
        <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-[#D4AF37]" data-testid={`courier-kitchen-status-${order.id}`}>
          Cuisine : {{ new: "en attente du restaurant", accepted: "acceptée", preparing: "en préparation", ready: "prête à récupérer", rejected: "refusée" }[order.restaurant_status]}
        </p>
      )}
      {/* Header row */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-black text-white">#{order.id.substring(0, 8)}</p>
            <span
              className={`text-[10px] px-2.5 py-1 rounded-full font-black uppercase tracking-widest ${badge.bg}`}
            >
              {badge.label}
            </span>
            {isCargo && (
              <span
                className="text-[10px] px-2.5 py-1 rounded-full bg-[#003594] text-white font-black uppercase tracking-widest"
                data-testid="cargo-badge"
              >
                CARGO · {order.vehicle_label || order.vehicle_type}
              </span>
            )}
          </div>
        </div>
        <p className="text-base font-black text-emerald-400">
          +{order.courier_earnings || order.courier_earning || order.delivery_fee || order.price || order.total_price || 0} FCFA
        </p>
      </div>

      <CargoDetails order={order} />

      {/* Recipient + destination */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] text-[#D4AF37] font-black uppercase tracking-[0.25em] mb-1">
            Destinataire
          </p>
          <p className="text-base font-black text-white">
            {order.recipient_name || order.customer_name}
          </p>
        </div>
        <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
          <p className="text-[10px] text-[#D4AF37] font-black uppercase tracking-[0.25em] mb-1">
            Arrivée
          </p>
          <p className="text-sm font-black text-white leading-snug">{order.delivery_address}</p>
        </div>
      </div>

      {/* Call client CTA */}
      {!["delivered", "cancelled"].includes(order.status) && phone && (
        <a
          href={`tel:${phone}`}
          className="flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white mb-3 px-5 py-3 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 transition-colors"
          data-testid={`call-client-${order.id}`}
        >
          <Phone className="w-4 h-4" /> Appeler le client · {phone}
        </a>
      )}

      {!["delivered", "cancelled"].includes(order.status) && wa && (
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5B] text-white mb-3 px-5 py-3 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-green-500/20 transition-colors"
          data-testid={`whatsapp-client-${order.id}`}
        >
          <MessageCircle className="w-4 h-4" /> Contacter le client sur WhatsApp
        </a>
      )}

      {/* Navigation GPS CTA — ouvre Google Maps/Waze sur l'adresse contextuelle */}
      {!["delivered", "cancelled"].includes(order.status) && (() => {
        const target = getNavTarget(order);
        const url = buildNavUrl(target.lat, target.lng, target.address);
        if (!url) return null;
        const hasGPS = target.lat != null && target.lng != null;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#003594] to-[#0046C6] hover:opacity-90 text-white mb-3 px-5 py-3 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-[#003594]/30 transition-opacity"
            data-testid={`navigate-${order.id}`}
          >
            <MapIcon className="w-4 h-4" />
            Naviguer · {target.label}
            {!hasGPS && <span className="text-[9px] opacity-70">(adresse)</span>}
          </a>
        );
      })()}

      {/* Carte intégrée temps réel — itinéraire OSRM recalculé toutes les 5s */}
      {order.status === "in_transit" && hasDestGPS && splitHidden && (
        <button
          onClick={() => setSplitHidden(false)}
          className="w-full mb-3 flex items-center justify-center gap-2 bg-[#003594] text-white px-5 py-3 rounded-2xl text-sm font-black uppercase tracking-widest"
          data-testid={`reopen-split-${order.id}`}
        >
          <MapIcon className="w-4 h-4" /> Itinéraire plein écran
        </button>
      )}

      {!["delivered", "cancelled"].includes(order.status) && hasDestGPS && !split && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setMapOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 bg-slate-900 border border-slate-800 hover:border-[#D4AF37]/40 px-4 py-3 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-300 hover:text-white transition-colors"
            data-testid={`toggle-mini-map-${order.id}`}
          >
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              {mapOpen ? "Masquer la carte" : "Afficher la carte temps réel"}
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${mapOpen ? "rotate-180" : ""}`}
            />
          </button>
          {mapOpen && (
            <div className="mt-2 rounded-2xl overflow-hidden border border-slate-800">
              <OrderMiniRouteMap
                orderId={order.id}
                destLat={navTarget.lat}
                destLng={navTarget.lng}
                destLabel={navTarget.label}
                height={240}
              />
            </div>
          )}
        </div>
      )}

      <PipelineActions order={order} onAdvance={onAdvance} />

      <RatingBlock
        order={order}
        rated={rated}
        ratingOrderId={ratingOrderId}
        ratingScore={ratingScore}
        onStartRating={onStartRating}
        onSetScore={onSetScore}
        onSubmit={onSubmitRating}
        onCancel={onCancelRating}
      />
    </div>
  );

  if (!split) return card;
  return (
    <>
      <div className="border-2 border-[#D4AF37]/40 rounded-2xl p-4 bg-[#0F172A] text-sm font-black text-white flex items-center justify-between">
        <span>#{order.id.substring(0, 8)} · En route</span>
      </div>
      <RouteSplitScreen order={order} dest={{ lat: navTarget.lat, lng: navTarget.lng }} onMinimize={() => setSplitHidden(true)}>
        {card}
      </RouteSplitScreen>
    </>
  );
};
